import { Listbox, Transition } from '@headlessui/react';
import { DesktopComputerIcon } from '@heroicons/react/outline';
import { CheckIcon, SelectorIcon } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import { Fragment } from 'react';
import { FaCrown } from 'react-icons/fa6';

import clsxm from '@/lib/clsxm';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface ResolutionSelectorProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  disabled?: boolean;
  lockedOptions?: string[];
  /**
   * When set, locked options stay selectable and picking one calls this
   * instead of `onChange` (e.g. to open an upgrade prompt).
   */
  onLockedSelect?: (option: string) => void;
}

export default function ResolutionSelector({
  value,
  onChange,
  options,
  disabled = false,
  lockedOptions = [],
  onLockedSelect,
}: ResolutionSelectorProps) {
  const { t } = useLocale('common');

  const normalizedValue =
    options.includes(value) && value ? value : options[0] || '';

  return (
    <div className='relative w-auto min-w-[90px]'>
      <Listbox
        value={normalizedValue}
        onChange={(option: string) => {
          if (onLockedSelect && lockedOptions.includes(option)) {
            onLockedSelect(option);
            return;
          }
          onChange(option);
        }}
        disabled={disabled || options.length === 0}
      >
        {() => (
          <>
            <div className='relative'>
              <Listbox.Button
                className={clsxm(
                  'relative w-full max-w-[110px] cursor-pointer rounded-xl border bg-white py-2 pe-8 ps-3 text-start shadow-sm focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm dark:bg-slate-700/50 dark:border-white/5',
                  disabled || options.length === 0
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-gray-50 dark:hover:bg-slate-700',
                )}
              >
                <SimpleTooltip title={t('Resolution')} side='bottom'>
                  <div className='flex items-center gap-1.5'>
                    <DesktopComputerIcon
                      className='h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400'
                      aria-hidden='true'
                    />
                    <span className='block truncate text-gray-700 dark:text-gray-200 font-medium'>
                      {normalizedValue}
                    </span>
                  </div>
                  <span className='pointer-events-none absolute inset-y-0 end-0 flex items-center pe-2'>
                    <SelectorIcon
                      className='h-5 w-5 text-gray-400'
                      aria-hidden='true'
                    />
                  </span>
                </SimpleTooltip>
              </Listbox.Button>

              <Transition
                as={Fragment}
                leave='transition ease-in duration-100'
                leaveFrom='opacity-100'
                leaveTo='opacity-0'
              >
                <Listbox.Options className='absolute bottom-full mb-1 start-0 z-50 w-full min-w-[200px] overflow-auto rounded-xl bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-slate-800 dark:ring-white/10'>
                  <div className='px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                    {t('Resolution')}
                  </div>
                  {options.map((option, optionIdx) => {
                    const isLocked = lockedOptions.includes(option);
                    return (
                      <Listbox.Option
                        key={optionIdx}
                        className={({ active }) =>
                          clsxm(
                            'relative cursor-default select-none py-2 pe-4 ps-3',
                            isLocked && !onLockedSelect
                              ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-500'
                              : active
                                ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-900 dark:text-indigo-100'
                                : isLocked
                                  ? 'text-gray-500 dark:text-gray-400'
                                  : 'text-gray-900 dark:text-gray-300',
                            (!isLocked || onLockedSelect) && 'cursor-pointer',
                          )
                        }
                        value={option}
                        disabled={isLocked && !onLockedSelect}
                      >
                        {({ selected }) => (
                          <div className='flex items-center justify-between'>
                            <span
                              className={clsxm(
                                'block truncate font-medium',
                                selected ? 'font-semibold' : 'font-normal',
                              )}
                            >
                              {option}
                            </span>
                            {isLocked ? (
                              <SimpleTooltip
                                title={t('Upgrade to unlock this resolution')}
                              >
                                <span>
                                  <FaCrown className='h-3.5 w-3.5 text-amber-400' />
                                </span>
                              </SimpleTooltip>
                            ) : selected ? (
                              <span className='text-indigo-600 dark:text-indigo-400 ms-2'>
                                <CheckIcon
                                  className='h-4 w-4'
                                  aria-hidden='true'
                                />
                              </span>
                            ) : null}
                          </div>
                        )}
                      </Listbox.Option>
                    );
                  })}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </div>
  );
}
